/**
 * D359: the landing scene. Opening the app shows "Idle Worlds" over a fully built world of a
 * random theme and seed (`landing-world.ts`), drawn by the 3D map and alive. It is the loading
 * screen too: a slim line creeps on while the app boots. Then it fades into what boot found:
 * an open world is greeted by name first; a first visit goes on to the first run's picker; a
 * world that cannot be reached goes on to its own message.
 *
 * Three.js loads lazily, as on the World tab, so the first paint is this page's text and the
 * map's backdrop. Under reduced motion, on a slow phone, or for a player who chose the flat
 * map, the world is the flat map's still picture with a slow pan, and three.js is not loaded.
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { now } from '../clock.ts';
import { prefersReducedMotion } from '../juice/motion.ts';
import { Button } from '../ui/Button.tsx';
import { IsoWorldMap } from '../ui/IsoWorldMap.tsx';
import { landingScene, randomWorld } from './landing-world.ts';
import { themeName } from './WorldRows.tsx';
import { idleContent, useIdle } from './store.ts';
import { defaultWorld, landingChooserSkipped, landingMinSkipped } from './world-pref.ts';
import { LazyThreeWorld } from './three/LazyThreeWorld.tsx';
import { isFlatMap } from './three/pref.ts';
import './idle.css';

/** The scene shows at least this long, so it is seen rather than flashed. */
const MIN_MS = 2500;
/** How long an open world's name is shown before the scene fades. */
const GREET_MS = 1400;
/** The fade, as `.idle-landing` has it. */
const FADE_MS = 600;

/** A phone that would struggle with the 3D scene, as far as the browser says. */
function slowDevice(): boolean {
  const n = navigator as Navigator & {
    deviceMemory?: number;
    connection?: { saveData?: boolean };
  };
  return (
    (n.hardwareConcurrency ?? 8) <= 2 || (n.deviceMemory ?? 8) <= 2 || !!n.connection?.saveData
  );
}

type Phase = 'boot' | 'choose' | 'greet' | 'out' | 'gone';

export function Landing() {
  const [world] = useState(randomWorld);
  const [still] = useState(() => prefersReducedMotion() || slowDevice() || isFlatMap());
  const scene = useMemo(() => landingScene(world.theme, world.seed, now()), [world]);
  const mounted = useRef(performance.now());
  const ready = useIdle((s) => !!s.world);
  const firstRun = useIdle((s) => s.firstRun !== null);
  const offline = useIdle((s) => s.offline);
  const worldName = useIdle((s) => s.worldName);
  const worlds = useIdle((s) => s.worlds);
  const [phase, setPhase] = useState<Phase>('boot');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // D375: after the minimum wait, a player with several worlds chooses one, unless "Open on
  // start" names one, which opens. One world and no default: it opens, as before.
  useEffect(() => {
    if (phase !== 'boot' || !(ready || firstRun || offline)) return;
    const wait = landingMinSkipped()
      ? 0
      : Math.max(0, MIN_MS - (performance.now() - mounted.current));
    let live = true;
    const id = setTimeout(async () => {
      if (!ready) return setPhase('out');
      await useIdle.getState().loadWorlds();
      if (!live) return;
      const mine = useIdle.getState().worlds.filter((f) => !f.archived);
      const pick = mine.find((f) => f.worldId === defaultWorld());
      if (pick && !pick.current) {
        const reason = await useIdle.getState().switchWorld(pick.worldId);
        if (!live) return;
        if (reason) {
          setError(reason);
          return setPhase('choose');
        }
        return setPhase('greet');
      }
      setPhase(!pick && mine.length > 1 && !landingChooserSkipped() ? 'choose' : 'greet');
    }, wait);
    return () => {
      live = false;
      clearTimeout(id);
    };
  }, [phase, ready, firstRun, offline]);

  const choose = async (worldId: string, current: boolean) => {
    setError(null);
    if (!current) {
      setBusy(true);
      const reason = await useIdle.getState().switchWorld(worldId);
      setBusy(false);
      if (reason) return setError(reason);
    }
    setPhase('greet');
  };
  const startNew = () => {
    useIdle.getState().closeWelcome();
    useIdle.getState().setNewWorldOpen(true);
    setPhase('out');
  };
  const mine = worlds.filter((f) => !f.archived);
  const room = mine.length < idleContent.worldsPerPlayer;

  useEffect(() => {
    if (phase !== 'greet' && phase !== 'out') return;
    const id = setTimeout(
      () => setPhase(phase === 'greet' ? 'out' : 'gone'),
      phase === 'greet' ? GREET_MS : FADE_MS,
    );
    return () => clearTimeout(id);
  }, [phase]);

  // D416: the welcome back waits until the landing has gone, so for the world chosen.
  useEffect(() => {
    useIdle.getState().setLandingOpen(phase !== 'gone');
  }, [phase]);

  if (phase === 'gone') return null;

  const flat = (
    <IsoWorldMap
      still
      theme={scene.theme}
      label="Idle Worlds"
      parcels={scene.parcels}
      buildings={scene.buildings}
      plot={scene.plot}
      worldPlot={scene.worldPlot}
      lands={scene.lands}
      country={scene.country}
      stream={scene.stream}
      showSigns={false}
      decor={scene.decor}
      style={{ position: 'absolute', inset: 0, height: '100%', aspectRatio: 'auto' }}
    />
  );
  const booting = phase === 'boot';

  return (
    <div
      data-testid="landing"
      data-theme-id={world.theme}
      data-still={still ? '1' : '0'}
      data-phase={phase}
      className={`idle-landing${phase === 'out' ? ' idle-landing-out' : ''}`}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 40,
        overflow: 'hidden',
        background: 'var(--map-backdrop)',
      }}
    >
      <div
        aria-hidden="true"
        className={still ? 'idle-landing-pan' : undefined}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        {still ? (
          flat
        ) : (
          <LazyThreeWorld
            label="Idle Worlds"
            theme={scene.theme}
            parcels={scene.parcels}
            buildings={scene.buildings}
            plot={scene.plot}
            worldPlot={scene.worldPlot}
            lands={scene.lands}
            country={scene.country}
            stream={scene.stream}
            showSigns={false}
            style={{ position: 'absolute', inset: 0 }}
            fallback={flat}
          />
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          padding: '72px 24px 32px',
          display: 'grid',
          gap: 12,
          justifyItems: 'center',
          textAlign: 'center',
          background: 'linear-gradient(var(--bg), transparent)',
        }}
      >
        <h1
          style={{
            margin: 0,
            font: '700 var(--fs-display)/1.1 var(--font-ui)',
            color: 'var(--ink)',
          }}
        >
          Idle Worlds
        </h1>
        <div
          role="progressbar"
          aria-label="Opening"
          aria-busy={booting}
          style={{
            width: 160,
            height: 3,
            borderRadius: 2,
            background: 'var(--line)',
            overflow: 'hidden',
          }}
        >
          <div
            data-testid="landing-progress"
            className={booting ? 'idle-landing-line' : undefined}
            style={{
              height: '100%',
              background: 'var(--accent)',
              ...(booting ? {} : { width: '100%', transition: 'width 300ms' }),
            }}
          />
        </div>
        {(phase === 'greet' || phase === 'out') && ready && worldName && (
          <p
            data-testid="landing-greeting"
            style={{
              margin: 0,
              font: '600 var(--fs-title)/1.3 var(--font-ui)',
              color: 'var(--ink)',
            }}
          >
            Welcome back to {worldName}
          </p>
        )}
      </div>
      {phase === 'choose' && (
        <div
          data-testid="world-chooser"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '16px 16px 32px',
            display: 'grid',
            gap: 10,
            maxHeight: '70%',
            overflowY: 'auto',
            background: 'linear-gradient(transparent, var(--bg) 24px)',
          }}
        >
          <h2
            style={{
              margin: 0,
              font: '600 var(--fs-title)/1.3 var(--font-ui)',
              color: 'var(--ink)',
            }}
          >
            Choose a world
          </h2>
          {mine.map((f) => (
            <button
              key={f.worldId}
              type="button"
              data-testid={`choose-${f.worldId}`}
              disabled={busy}
              onClick={() => void choose(f.worldId, f.current)}
              style={{
                display: 'grid',
                gap: 2,
                minHeight: 56,
                padding: '8px 14px',
                textAlign: 'left',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--line)',
                background: 'var(--bg)',
                color: 'var(--ink)',
                font: '600 var(--fs-name)/1.3 var(--font-ui)',
                cursor: 'pointer',
              }}
            >
              <span>{f.name}</span>
              <span style={{ font: '400 13px/1.4 var(--font-ui)', color: 'var(--ink-2)' }}>
                {themeName(f.theme)}
                {f.year ? `, year ${f.year}` : ''}
              </span>
            </button>
          ))}
          {room && (
            <Button variant="secondary" full data-testid="choose-new" onClick={startNew}>
              Start a new world
            </Button>
          )}
          {error && (
            <p role="alert" style={{ margin: 0, font: '400 13px/1.4 var(--font-ui)' }}>
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
